
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, TrendingUp, Target } from 'lucide-react';

interface OverallScoreCardProps {
  score: number;
  error?: string;
}

export const OverallScoreCard: React.FC<OverallScoreCardProps> = ({ 
  score,
  error 
}) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Ensure we're displaying a valid score out of 100
  const displayScore = score ? Math.min(100, Math.round(score)) : 0;
  const hasError = error !== undefined;
  
  // Animate score on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedScore(displayScore);
    }, 300);
    return () => clearTimeout(timer);
  }, [displayScore]);
  
  // Determine score color and gradient based on value
  const getScoreColor = () => {
    if (hasError || displayScore === 0) return "text-red-500";
    if (displayScore >= 80) return "text-emerald-500";
    if (displayScore >= 70) return "text-lime-500";
    if (displayScore >= 60) return "text-yellow-500";
    if (displayScore >= 50) return "text-orange-500";
    return "text-red-500";
  };
  
  const getScoreGradient = () => {
    if (hasError || displayScore === 0) return "from-red-500/20 to-red-600/10";
    if (displayScore >= 80) return "from-emerald-500/20 to-green-600/10";
    if (displayScore >= 70) return "from-lime-500/20 to-green-500/10";
    if (displayScore >= 60) return "from-yellow-500/20 to-amber-500/10";
    if (displayScore >= 50) return "from-orange-500/20 to-red-500/10";
    return "from-red-500/20 to-red-600/10";
  };
  
  const getPerformanceLevel = () => {
    if (hasError || displayScore === 0) return "Needs Attention";
    if (displayScore >= 80) return "Excellent";
    if (displayScore >= 70) return "Good";
    if (displayScore >= 60) return "Fair";
    if (displayScore >= 50) return "Poor";
    return "Critical";
  };
  
  const getIcon = () => {
    if (hasError || displayScore === 0) return AlertTriangle;
    if (displayScore >= 70) return TrendingUp;
    return Target;
  };
  
  const Icon = getIcon();
  
  return (
    <Card 
      className="overflow-hidden widget-container cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between mb-2">
          <Icon className={`h-6 w-6 ${getScoreColor()} transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`} />
          <div className={`px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getScoreGradient()} border border-current/20`}>
            {getPerformanceLevel()}
          </div>
        </div>
        
        <CardTitle className="text-center">
          <div className="relative mb-3 sm:mb-4">
            {/* Circular Progress Ring */}
            <div className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                {/* Background ring */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="hsl(var(--border))"
                  strokeWidth="8"
                  fill="none"
                  className="opacity-30"
                />
                {/* Progress ring */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 45}`}
                  strokeDashoffset={`${2 * Math.PI * 45 * (1 - animatedScore / 100)}`}
                  className={`${getScoreColor()} transition-all duration-1000 ease-out drop-shadow-sm`}
                />
              </svg>
              {/* Score text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-2xl sm:text-3xl font-bold ${getScoreColor()}`}>
                  {animatedScore}
                </span>
                <span className="text-xs text-muted-foreground">out of 100</span>
              </div>
            </div>
          </div>
        </CardTitle>
        
        <CardDescription className="text-center">
          <div className="text-base sm:text-lg font-medium mb-1">Overall Performance</div>
          {(hasError || displayScore === 0) && (
            <div className="flex items-center justify-center gap-2 mt-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <span className="text-amber-600 dark:text-amber-400 text-xs sm:text-sm">
                {hasError ? "Analysis error" : "Analysis failed to produce scores"}
              </span>
            </div>
          )}
        </CardDescription>
      </CardHeader>
    </Card>
  );
};
